import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import { supabase } from '../../lib/supabase';

import MachineInventoryTable from './components/MachineInventoryTable';
import MachineCard from './components/MachineCard';
import MaintenanceSchedule from './components/MaintenanceSchedule';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import ServiceHistoryModal from './components/ServiceHistoryModal';
import SparePartsInventory from './components/SparePartsInventory';
import Icon from '../../components/AppIcon';

// ─── Normalizers: map DB fields → component field names ───────────────────────

const normalizeMachine = (m) => ({
  ...m,
  name:           m.machine_name,
  serialNumber:   m.machine_code,
  utilizationRate: m.current_utilization ?? 0,
  successRate:    m.current_utilization ?? 0,
  totalPrintHours: m.operating_hours ?? 0,
  nextMaintenance: m.next_maintenance_date,
  lastMaintenance: m.last_maintenance_date,
  location:       m.location,
  model:          m.model,
  manufacturer:   m.manufacturer,
  status:         m.status,
  notes:          m.notes,
  supplier: m.supplier_profile
    ? {
        companyName: m.supplier_profile.company_name,
        fullName:    m.supplier_profile.full_name,
        email:       m.supplier_profile.email,
      }
    : null,
});

const normalizeMaintenanceItem = (item, machinesMap) => ({
  ...item,
  machineName:       machinesMap[item.machine_id] || 'Machine inconnue',
  type:              item.maintenance_type,
  scheduledDate:     item.scheduled_date,
  estimatedDuration: item.duration_hours ? `${item.duration_hours}h` : '—',
  technician:        item.technician_name || '—',
  status:            item.status,
  priority:          'medium',
  description:       item.notes || '',
});

const normalizeSparePart = (p) => {
  const stock = p.quantity_in_stock ?? 0;
  const min   = p.minimum_stock_level ?? 0;
  const status = stock === 0 ? 'out_of_stock' : stock <= min ? 'low_stock' : 'in_stock';
  return {
    ...p,
    name:          p.part_name,
    partNumber:    p.part_code,
    stockLevel:    stock,
    minStockLevel: min,
    unitPrice:     p.unit_price,
    lastOrdered:   p.last_restocked_date,
    status,
  };
};

// ─────────────────────────────────────────────────────────────────────────────

// ─── FIXED: Field component defined OUTSIDE the modal ────────────────────────

const Field = ({ label, field, type = 'text', required, children, value, onChange, error }) => {
  if (children) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-foreground">
          {label}{required && <span className="text-error ml-1">*</span>}
        </label>
        {children}
        {error && <span className="text-xs text-error">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-foreground">
        {label}{required && <span className="text-error ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={onChange}
        className={`px-3 py-2 border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
          error ? 'border-error' : 'border-border'
        }`}
      />
      {error && <span className="text-xs text-error">{error}</span>}
    </div>
  );
};

// ─── Machine Form Modal ──────────────────────────────────────────────────────

const MACHINE_STATUS_OPTIONS = [
  { value: 'operational',  label: 'Opérationnelle' },
  { value: 'maintenance',  label: 'Maintenance'    },
  { value: 'offline',      label: 'Hors ligne'     },
];

const MachineFormModal = ({ machine, suppliers, onSave, onClose }) => {
  const isEdit = !!machine;
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    machine_name:          machine?.machine_name         || '',
    machine_code:          machine?.machine_code         || '',
    manufacturer:          machine?.manufacturer         || '',
    model:                 machine?.model                || '',
    status:                machine?.status               || 'operational',
    location:              machine?.location             || '',
    installation_date:     machine?.installation_date?.slice(0, 10) || '',
    production_capacity:   machine?.production_capacity  != null ? String(machine.production_capacity) : '',
    operating_hours:       machine?.operating_hours      != null ? String(machine.operating_hours)     : '0',
    current_utilization:   machine?.current_utilization  != null ? String(machine.current_utilization) : '0',
    last_maintenance_date: machine?.last_maintenance_date?.slice(0, 10) || '',
    next_maintenance_date: machine?.next_maintenance_date?.slice(0, 10) || '',
    supplier_id:           machine?.supplier_id          || '',
    notes:                 machine?.notes                || '',
  });

  // ✅ FIXED: Use useCallback to prevent function recreation
  const handleChange = useCallback((field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = () => {
    const errs = {};
    if (!form.machine_name.trim())      errs.machine_name       = 'Champ requis';
    if (!form.machine_code.trim())      errs.machine_code       = 'Champ requis';
    if (!form.manufacturer.trim())      errs.manufacturer       = 'Champ requis';
    if (!form.model.trim())             errs.model              = 'Champ requis';
    if (!form.location.trim())          errs.location           = 'Champ requis';
    if (!form.installation_date)        errs.installation_date  = 'Champ requis';
    if (!form.production_capacity)      errs.production_capacity = 'Champ requis';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { 
      setErrors(errs); 
      return; 
    }

    setSaving(true);
    try {
      await onSave({
        machine_name:          form.machine_name.trim(),
        machine_code:          form.machine_code.trim(),
        manufacturer:          form.manufacturer.trim(),
        model:                 form.model.trim(),
        status:                form.status,
        location:              form.location.trim(),
        installation_date:     form.installation_date,
        production_capacity:   Number(form.production_capacity),
        operating_hours:       Number(form.operating_hours) || 0,
        current_utilization:   Math.min(100, Math.max(0, Number(form.current_utilization) || 0)),
        last_maintenance_date: form.last_maintenance_date || null,
        next_maintenance_date: form.next_maintenance_date || null,
        supplier_id:           form.supplier_id || null,
        notes:                 form.notes.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isEdit ? 'bg-warning/10' : 'bg-primary/10'}`}>
              <Icon name={isEdit ? 'Pencil' : 'Plus'} size={18} className={isEdit ? 'text-warning' : 'text-primary'} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit ? 'Modifier la machine' : 'Ajouter une machine'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {isEdit ? `${machine.machine_name} · ${machine.machine_code}` : 'Remplissez les informations de la nouvelle machine'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">

          {/* Identification */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Identification
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field 
                label="Nom de la machine" 
                field="machine_name" 
                value={form.machine_name}
                onChange={handleChange('machine_name')}
                error={errors.machine_name}
                required 
              />
              <Field 
                label="Code machine" 
                field="machine_code" 
                value={form.machine_code}
                onChange={handleChange('machine_code')}
                error={errors.machine_code}
                required 
              />
              <Field 
                label="Fabricant" 
                field="manufacturer" 
                value={form.manufacturer}
                onChange={handleChange('manufacturer')}
                error={errors.manufacturer}
                required 
              />
              <Field 
                label="Modèle" 
                field="model" 
                value={form.model}
                onChange={handleChange('model')}
                error={errors.model}
                required 
              />
            </div>
          </div>

          {/* Configuration */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Configuration
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field 
                label="Localisation" 
                field="location" 
                value={form.location}
                onChange={handleChange('location')}
                error={errors.location}
                required 
              />
              <Field label="Statut" field="status" required>
                <select
                  value={form.status}
                  onChange={handleChange('status')}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {MACHINE_STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
              <Field 
                label="Date d'installation" 
                field="installation_date" 
                type="date" 
                value={form.installation_date}
                onChange={handleChange('installation_date')}
                error={errors.installation_date}
                required 
              />
              <Field 
                label="Capacité de production" 
                field="production_capacity" 
                type="number" 
                value={form.production_capacity}
                onChange={handleChange('production_capacity')}
                error={errors.production_capacity}
                required 
              />
              <Field 
                label="Heures de fonctionnement" 
                field="operating_hours" 
                type="number" 
                value={form.operating_hours}
                onChange={handleChange('operating_hours')}
              />
              <Field 
                label="Utilisation actuelle (0–100 %)" 
                field="current_utilization" 
                type="number" 
                value={form.current_utilization}
                onChange={handleChange('current_utilization')}
              />
            </div>
          </div>

          {/* Maintenance */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Maintenance
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field 
                label="Dernière maintenance" 
                field="last_maintenance_date" 
                type="date" 
                value={form.last_maintenance_date}
                onChange={handleChange('last_maintenance_date')}
              />
              <Field 
                label="Prochaine maintenance" 
                field="next_maintenance_date" 
                type="date" 
                value={form.next_maintenance_date}
                onChange={handleChange('next_maintenance_date')}
              />
            </div>
          </div>

          {/* Fournisseur */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Fournisseur
            </h3>
            <Field label="Fournisseur associé *" field="supplier_id">
              <select
                value={form.supplier_id}
                onChange={handleChange('supplier_id')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Aucun fournisseur —</option>
                {suppliers?.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.company_name || s.full_name || s.email}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={handleChange('notes')}
              rows={3}
              placeholder="Informations complémentaires..."
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button iconName={isEdit ? 'Pencil' : 'Save'} onClick={handleSubmit} loading={saving} disabled={saving}>
            {saving
              ? (isEdit ? 'Mise à jour...' : 'Enregistrement...')
              : (isEdit ? 'Mettre à jour' : 'Enregistrer la machine')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const MachineFleetManagement = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [viewMode, setViewMode] = useState('table');
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [showServiceHistory, setShowServiceHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [machines, setMachines] = useState([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [showAddMachine, setShowAddMachine] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Gestion Parc Machines', path: '/machine-fleet-management', icon: 'Settings' }
  ];

  useEffect(() => {
    fetchMachines();
    fetchSpareParts();
    fetchSuppliers();
  }, []);

  // ── Fetch machines ──────────────────────────────────────────────────────────
  const fetchMachines = async (supplierFilter = selectedSupplier) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('machines')
        .select(`
          *,
          supplier_profile:user_profiles!supplier_id (
            id,
            full_name,
            company_name,
            email
          )
        `)
        .order('machine_name', { ascending: true });

      if (supplierFilter && supplierFilter !== 'all') {
        query = query.eq('supplier_id', supplierFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      setMachines((data || []).map(normalizeMachine));
    } catch (err) {
      console.error('Error fetching machines:', err);
      setError('Erreur lors du chargement des machines');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch spare parts ───────────────────────────────────────────────────────
  const fetchSpareParts = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('spare_parts')
        .select('*')
        .order('part_name', { ascending: true });

      if (fetchError) throw fetchError;
      setSpareParts((data || []).map(normalizeSparePart));
    } catch (err) {
      console.error('Error fetching spare parts:', err);
      setSpareParts([]);
    }
  };

  // ── Fetch suppliers (user_profiles with supplier role) ──────────────────────
  const fetchSuppliers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('id, full_name, company_name, email')
        .eq('is_active', true)
        .order('full_name', { ascending: true });

      if (fetchError) throw fetchError;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setSuppliers([]);
    }
  };

  const handleSupplierFilterChange = (supplierId) => {
    setSelectedSupplier(supplierId);
    fetchMachines(supplierId);
  };

  // ── Create machine ──────────────────────────────────────────────────────────
  const handleCreateMachine = async (machineData) => {
    try {
      const { error: insertError } = await supabase
        .from('machines')
        .insert([machineData]);

      if (insertError) throw insertError;
      await fetchMachines();
    } catch (err) {
      console.error('Error creating machine:', err);
      alert('Erreur lors de la création de la machine');
    }
  };

  // ── Update machine ──────────────────────────────────────────────────────────
  const handleUpdateMachine = async (id, machineData) => {
    try {
      const { error: updateError } = await supabase
        .from('machines')
        .update(machineData)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchMachines();
    } catch (err) {
      console.error('Error updating machine:', err);
      alert('Erreur lors de la mise à jour de la machine');
    }
  };

  // ── Delete machine ──────────────────────────────────────────────────────────
  const handleDeleteMachine = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette machine ?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('machines')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchMachines();
    } catch (err) {
      console.error('Error deleting machine:', err);
      alert('Erreur lors de la suppression de la machine');
    }
  };

  // ── Update machine status ───────────────────────────────────────────────────
  const handleUpdateStatus = async (id, status) => {
    try {
      const { error: updateError } = await supabase
        .from('machines')
        .update({ status })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchMachines();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // ── Schedule maintenance ────────────────────────────────────────────────────
  const handleScheduleMaintenance = async (id, maintenanceData) => {
    try {
      const { error: insertError } = await supabase
        .from('maintenance_schedule')
        .insert([{
          machine_id:       id,
          maintenance_type: maintenanceData.type || maintenanceData.maintenance_type,
          scheduled_date:   maintenanceData.scheduledDate || maintenanceData.scheduled_date,
          technician_name:  maintenanceData.technician || maintenanceData.technician_name,
          duration_hours:   maintenanceData.estimatedDuration || maintenanceData.duration_hours,
          notes:            maintenanceData.description || maintenanceData.notes,
          status:           'scheduled',
        }]);

      if (insertError) throw insertError;
      alert('Maintenance programmée avec succès');
    } catch (err) {
      console.error('Error scheduling maintenance:', err);
      alert('Erreur lors de la programmation de la maintenance');
    }
  };

  // ── Export CSV ──────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = [
      'Nom', 'Code', 'Fabricant', 'Modèle', 'Statut',
      'Localisation', 'Utilisation (%)', 'Heures fonctionnement',
      'Capacité production', 'Dernière maintenance', 'Prochaine maintenance',
      'Fournisseur', 'Notes'
    ];

    const rows = machines.map(m => [
      m.machine_name,
      m.machine_code,
      m.manufacturer,
      m.model,
      m.status,
      m.location,
      m.current_utilization ?? 0,
      m.operating_hours ?? 0,
      m.production_capacity ?? 0,
      m.last_maintenance_date || '',
      m.next_maintenance_date || '',
      m.supplier?.companyName || m.supplier?.fullName || '',
      m.notes || '',
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `machines_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewServiceHistory = async (machine) => {
    setSelectedMachine(machine);

    try {
      const { data, error: fetchError } = await supabase
        .from('maintenance_schedule')
        .select('*')
        .eq('machine_id', machine?.id)
        .order('scheduled_date', { ascending: false });

      if (fetchError) throw fetchError;

      const machinesMap = Object.fromEntries(
        machines.map(m => [m.id, m.name || m.machine_name])
      );
      setMaintenanceSchedule((data || []).map(item => normalizeMaintenanceItem(item, machinesMap)));
    } catch (err) {
      console.error('Error fetching maintenance schedule:', err);
      setMaintenanceSchedule([]);
    }

    setShowServiceHistory(true);
  };

  // ── Stats ───────────────────────────────────────────────────────────────────
  const operationalCount = machines?.filter(m => m?.status === 'operational')?.length;
  const availableCount   = machines?.filter(m => m?.status === 'available')?.length;
  const maintenanceCount = machines?.filter(m => m?.status === 'maintenance')?.length;
  const totalMachines    = machines?.length;

  const averageUtilization = machines?.length
    ? Math.round(machines.reduce((sum, m) => sum + (m?.utilizationRate ?? 0), 0) / machines.length)
    : 0;

  const averageSuccessRate = machines?.length
    ? (machines.reduce((sum, m) => sum + (m?.successRate ?? 0), 0) / machines.length).toFixed(1)
    : '0.0';

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-3 sm:px-4 py-4 md:py-6 max-w-[1400px] min-w-0">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
              <div className="min-w-0">
                <Icon name="Settings" size={24} className="text-primary flex-shrink-0" />
                <h1 className="text-xl md:text-2xl font-bold text-foreground mt-1">Gestion du Parc Machines</h1>
                <p className="text-sm md:text-base text-muted-foreground">Inventaire et maintenance des équipements de production</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                <select
                  value={selectedSupplier}
                  onChange={(e) => handleSupplierFilterChange(e?.target?.value)}
                  className="min-touch-target px-4 py-2.5 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-auto"
                >
                  <option value="all">Tous les fournisseurs</option>
                  {suppliers?.map((supplier) => (
                    <option key={supplier?.id} value={supplier?.id}>
                      {supplier?.company_name || supplier?.full_name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <Button variant="outline" size="lg" iconName="Download" onClick={handleExport} className="min-touch-target flex-1 sm:flex-none">
                    Exporter
                  </Button>
                  <Button size="lg" iconName="Plus" onClick={() => setShowAddMachine(true)} className="min-touch-target flex-1 sm:flex-none">
                    Ajouter Machine
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                Chargement des machines...
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-error mb-4">{error}</p>
                <Button onClick={fetchMachines}>Réessayer</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                  <div className="bg-card border border-border rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Total Machines</span>
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="text-xl">🏭</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-foreground">{totalMachines}</div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Opérationnelles</span>
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <span className="text-xl">✅</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-success">{operationalCount}</div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Utilisation Moyenne</span>
                      <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                        <span className="text-xl">📊</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-foreground">{averageUtilization}%</div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Taux de Réussite</span>
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="text-xl">🎯</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-foreground">{averageSuccessRate}%</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">Inventaire des Machines</h2>
                  <div className="flex gap-2">
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="lg"
                      iconName="List"
                      onClick={() => setViewMode('table')}
                      className="min-touch-target flex-1 sm:flex-none"
                    >
                      Tableau
                    </Button>
                    <Button
                      variant={viewMode === 'cards' ? 'default' : 'outline'}
                      size="lg"
                      iconName="LayoutGrid"
                      onClick={() => setViewMode('cards')}
                      className="min-touch-target flex-1 sm:flex-none"
                    >
                      Cartes
                    </Button>
                  </div>
                </div>

                {viewMode === 'table' ? (
                  <MachineInventoryTable
                    machines={machines}
                    onViewHistory={handleViewServiceHistory}
                    onUpdate={handleUpdateMachine}
                    onDelete={handleDeleteMachine}
                    onStatusChange={handleUpdateStatus}
                    onEdit={(machine) => setEditingMachine(machine)}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {machines?.map(machine => (
                      <MachineCard
                        key={machine?.id}
                        machine={machine}
                        onViewHistory={handleViewServiceHistory}
                        onUpdate={(data) => handleUpdateMachine(machine?.id, data)}
                        onDelete={() => handleDeleteMachine(machine?.id)}
                        onEdit={() => setEditingMachine(machine)}
                        onStatusChange={(id, status) => handleUpdateStatus(id, status)}
                      />
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  <MaintenanceSchedule
                    schedule={maintenanceSchedule}
                    onSchedule={handleScheduleMaintenance}
                  />
                </div>

                <PerformanceAnalytics machines={machines} />
              </>
            )}
          </div>
        </main>
      </div>
      {showServiceHistory && selectedMachine && (
        <ServiceHistoryModal
          machine={selectedMachine}
          onClose={() => {
            setShowServiceHistory(false);
            setSelectedMachine(null);
          }}
        />
      )}
      {showAddMachine && (
        <MachineFormModal
          suppliers={suppliers}
          onSave={async (formData) => {
            await handleCreateMachine(formData);
            setShowAddMachine(false);
          }}
          onClose={() => setShowAddMachine(false)}
        />
      )}
      {editingMachine && (
        <MachineFormModal
          machine={editingMachine}
          suppliers={suppliers}
          onSave={async (formData) => {
            await handleUpdateMachine(editingMachine.id, formData);
            setEditingMachine(null);
          }}
          onClose={() => setEditingMachine(null)}
        />
      )}
    </div>
  );
};

export default MachineFleetManagement;